import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import s from './NovaCommissionModal.module.sass';
import { Amount, NovaQr, AddressUnit, BankTransferCard, RecipientGrid, closeUnlessAdminConsumed } from './modalKit';
import BankLogo from '../components/BankLogo';
import FlagIcon from '../../../components/FlagIcon/FlagIcon';
import {
    Landmark, X, ArrowRight, UserRound,
    CreditCard, Banknote, Coins, Info, CheckCircle2,
} from 'lucide-react';

/**
 * NovaBankConversionModal — «банковский перевод»: пара близнец крипто-
 * конвертации, но история своя. Фиат уже сконвертирован, теперь сумма
 * уходит из биржи в банк клиента, и банковская система требует сбор
 * за обработку вывода.
 *
 * Тот же тикет-ордер с янтарным акцентом: пара «валюта → банк»
 * (сумма одна и та же — она не меняется, меняется место), по шву — сбор.
 * Ключи — modals.bankTransfer.*, кошелёк — bankConversion.
 * Кнопка действия — «обработать вывод», после неё владелец открывает
 * модал успеха (логика App.js не меняется, прокинута через onPay).
 */
export default function NovaBankConversionModal({
    isClosing,
    onClose,
    safeT,
    siteSettings,
    title,
    // тикет-ордер
    processingTitle,
    statusLabel,
    processingNote,
    currencyCode,
    fromAmount,
    bankName,
    bankIconSrc,
    toAmount,
    // сбор за обработку
    feeTitle,
    feeDescription,
    feeAmount,
    // оплата
    paymentTitle,
    addressDisplay,
    networkInfoHtml,
    qrValue,
    canHideAddress,
    isAddressHidden,
    onToggleAddressHidden,
    isCopied,
    onCopy,
    // банковский режим оплаты
    bankMode,
    payAccountLabel,
    payBankName,
    payAccountNumber,
    payFields,
    payBankIconSrc,
    bankPaymentTitle,
    // получатель
    recipientTitle,
    nameLabel,
    nameUser,
    netAmountLabel,
    netAmount,
    bankLabel,
    accountLabel,
    accountValue,
    // действия
    payLabel,
    onPay,
}) {
    const [host, setHost] = useState(null);

    useEffect(() => {
        setHost(document.querySelector('[data-nova-theme]'));
    }, []);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    if (!host) return null;

    const recipientRows = [
        { Icon: UserRound, label: nameLabel, value: nameUser },
        { Icon: Banknote, label: netAmountLabel, value: netAmount, mono: true },
        { Icon: Landmark, label: bankLabel, value: bankName, logo: bankIconSrc },
        { Icon: CreditCard, label: accountLabel, value: accountValue, mono: true },
    ];

    return createPortal(
        <div
            className={`${s.overlay} ${isClosing ? s.overlayOut : ''}`}
            onClick={closeUnlessAdminConsumed(onClose)}
        >
            <div
                className={`${s.sheet} ${s.sheetMid} ${isClosing ? s.sheetOut : ''}`}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                onClick={(e) => e.stopPropagation()}
            >
                <header className={s.head}>
                    <span className={`${s.headIcon} ${s.headIconWarn}`}>
                        <Landmark size={15} strokeWidth={2.2} aria-hidden="true" />
                    </span>
                    <h2 className={s.headTitle}>{title}</h2>
                    <button
                        type="button"
                        className={s.closeBtn}
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <X size={15} strokeWidth={2.2} aria-hidden="true" />
                    </button>
                </header>

                <div className={s.body}>
                    {/* ── Тикет-ордер: «валюта → банк» + сбор по шву.
                        Сумма одна и та же с обеих сторон — деньги не меняются,
                        меняется место: с баланса биржи на счёт в банке */}
                    <div className={s.order}>
                        <div className={s.orderHead}>
                            <span className={s.eyebrow}>
                                <Landmark size={11} strokeWidth={2.4} aria-hidden="true" />
                                {processingTitle}
                            </span>
                            <span className={`${s.eyebrow} ${s.eyebrowWarn}`}>
                                <span className={`${s.pulse} ${s.pulseWarn}`} aria-hidden="true" />
                                {statusLabel}
                            </span>
                        </div>

                        <div className={s.orderPair}>
                            <div className={s.pairLeg}>
                                {/* Флаг страны затемнённым фоном, код валюты светлым поверх */}
                                <span className={`${s.coinPlate} ${s.coinPlateFlag} ${s.coinPlateFlagDim}`}>
                                    <FlagIcon
                                        siteSettings={siteSettings}
                                        className={s.plateFlag}
                                        alt=""
                                    />
                                    <b>{currencyCode}</b>
                                </span>
                                <span className={s.pairInfo}>
                                    <span className={s.pairSym}>{currencyCode}</span>
                                    <span className={`${s.pairCrypto} ${s.pairCryptoLg} ${s.pairCryptoWarn}`}>{fromAmount}</span>
                                </span>
                            </div>

                            <div className={s.pairFlow} aria-hidden="true">
                                <span className={s.pairArrow}>
                                    <ArrowRight size={13} strokeWidth={2.6} />
                                </span>
                            </div>

                            <div className={s.pairLeg}>
                                <span className={`${s.coinPlate} ${s.coinPlateBank}`}>
                                    {bankIconSrc
                                        ? <BankLogo src={bankIconSrc} alt={bankName} />
                                        : <Landmark size={18} strokeWidth={1.9} aria-hidden="true" />}
                                </span>
                                <span className={s.pairInfo}>
                                    <span className={s.pairSym}>{bankName}</span>
                                    <span className={`${s.pairCrypto} ${s.pairCryptoLg} ${s.pairCryptoGood}`}>
                                        <span className={s.pairCur}>{currencyCode}</span>
                                        {toAmount}
                                    </span>
                                </span>
                            </div>
                        </div>

                        <div className={s.orderFee}>
                            <span className={s.orderFeeLabel}>
                                <span className={s.orderFeeIcon}>
                                    <Coins size={13} strokeWidth={2.1} aria-hidden="true" />
                                </span>
                                <span className={s.orderFeeText}>
                                    <b>{feeTitle}</b>
                                    <em>{feeDescription}</em>
                                </span>
                            </span>
                            <Amount text={feeAmount} tone="fee" />
                        </div>
                    </div>

                    <p className={s.verifyCaption}>
                        <Info size={13} strokeWidth={2.1} aria-hidden="true" />
                        <span>{processingNote}</span>
                    </p>

                    {/* ── Оплата: QR + адрес, группа по центру листа */}
                    <div className={`${s.deposit} ${s.depositCentered} ${bankMode ? s.depositBank : ''}`}>
                        {bankMode ? (
                            <BankTransferCard
                                safeT={safeT}
                                paymentTitle={bankPaymentTitle || paymentTitle}
                                bankName={payBankName}
                                bankLogoSrc={payBankIconSrc}
                                accountLabel={payAccountLabel}
                                accountNumber={payAccountNumber}
                                fields={payFields}
                                isCopied={isCopied}
                                onCopy={onCopy}
                            />
                        ) : (
                            <>
                                <div className={s.qrFrame}>
                                    <NovaQr value={qrValue} size={96} />
                                </div>
                                <AddressUnit
                                    safeT={safeT}
                                    paymentTitle={paymentTitle}
                                    addressDisplay={addressDisplay}
                                    networkInfoHtml={networkInfoHtml}
                                    canHideAddress={canHideAddress}
                                    isAddressHidden={isAddressHidden}
                                    onToggleAddressHidden={onToggleAddressHidden}
                                    isCopied={isCopied}
                                    onCopy={onCopy}
                                />
                            </>
                        )}
                    </div>

                    <hr className={s.bodyDivider} aria-hidden="true" />

                    {/* ── Справка: куда придут деньги после обработки */}
                    <RecipientGrid title={recipientTitle} rows={recipientRows} />
                </div>

                {/* Единственное действие: обработать вывод (открывает модал успеха) */}
                <footer className={s.foot}>
                    <div className={`${s.footActions} ${s.footActionsSolo}`}>
                        <button type="button" className={s.primaryBtn} onClick={onPay}>
                            <CheckCircle2 size={15} strokeWidth={2.2} aria-hidden="true" />
                            {payLabel}
                        </button>
                    </div>
                </footer>
            </div>
        </div>,
        host,
    );
}
